import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { DEEP_LINK_PARAMS } from '@src/constants/deep-links';

/** True when two URLs share a path and differ only in deep-link params (opening or closing a modal). */
function changesOnlyDeepLinks(from: string, to: string): boolean {
  const fromUrl = new URL(from, window.location.origin);
  const toUrl = new URL(to, window.location.origin);
  const params = Array.from(fromUrl.searchParams.keys()).concat(Array.from(toUrl.searchParams.keys()));

  return (
    fromUrl.pathname === toUrl.pathname &&
    params.every(
      (param) =>
        DEEP_LINK_PARAMS.has(param) ||
        fromUrl.searchParams.getAll(param).join() === toUrl.searchParams.getAll(param).join(),
    )
  );
}

/**
 * Keeps the back button from reloading the page when it only closes a deep link (a modal, section or
 * feature in `deepLinks`).
 *
 * The Pages Router reruns `getServerSideProps` on a back unless both the entry it returns to and the
 * navigation that left it were shallow, and it never records a page's own entry as shallow. So closing
 * a deep-link modal by going back reloaded the whole trip behind the progress bar. Deep-link params
 * are client state, so a back that changes only them is handled here as the shallow replace it is.
 *
 * The App Router has none of this: a search-param change there never reruns the server. Delete this
 * hook once the trip pages move to it.
 */
export function useDeepLinkBackNavigation() {
  const router = useRouter();

  useEffect(() => {
    router.beforePopState(({ url, as }) => {
      if (!changesOnlyDeepLinks(router.asPath, as)) {
        return true;
      }

      void router.replace(url, as, { shallow: true, scroll: false });
      return false;
    });

    return () => router.beforePopState(() => true);
  }, [router]);
}
